import { Check, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Props = {
  label?: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
};

/** Dropdown multi-select con búsqueda. Cierre con click afuera. */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = "Selecciona…",
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (o: string) => {
    if (selected.includes(o)) {
      onChange(selected.filter((s) => s !== o));
    } else {
      onChange([...selected, o]);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <span className="text-xs font-medium text-slate-600 block mb-1">{label}</span>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="border rounded px-2 py-1.5 w-full text-sm text-left flex items-center justify-between hover:bg-slate-50"
      >
        <span className={selected.length === 0 ? "text-slate-400" : ""}>
          {selected.length === 0
            ? placeholder
            : selected.length === 1
              ? truncate(selected[0], 40)
              : `${selected.length} seleccionados`}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border rounded shadow-lg max-h-96 flex flex-col">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar…"
                className="border rounded pl-8 pr-2 py-1.5 w-full text-sm"
              />
            </div>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-slate-500 hover:text-slate-900 mt-1 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Limpiar ({selected.length})
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500 text-center">
                Sin resultados
              </div>
            )}
            {filtered.map((o) => {
              const isSel = selected.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => toggle(o)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                    isSel ? "bg-blue-50" : "hover:bg-slate-50"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSel ? "bg-blue-600 border-blue-600" : "bg-white border-slate-300"
                    }`}
                  >
                    {isSel && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="truncate">{o}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
